import React from "react";
import { useProfile } from "@/hooks/use-profile";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { 
  UserRound, Mail, Phone, MapPin, Globe, FileEdit, LogOut,
  Award, Clock, Calendar, Shield, BookOpen
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { AdminButton } from "@/components/profile/actions/AdminButton";

const ProfilePage = () => {
  const { 
    loading, 
    fullName,
    email,
    phone,
    address,
    website,
    bio,
    type,
    profilePhoto,
  } = useProfile();
  
  const navigate = useNavigate();
  const { toast } = useToast();

  const handleSignOut = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;

      navigate("/");
      
      toast({
        title: "Déconnexion réussie",
        description: "Vous avez été déconnecté avec succès.",
      });
    } catch (error: any) {
      console.error("Erreur lors de la déconnexion:", error);
      toast({
        variant: "destructive",
        title: "Erreur de déconnexion",
        description: "Une erreur est survenue lors de la déconnexion.",
      });
    }
  };

  const initials = fullName
    ? fullName
        .split(" ")
        .map((n) => n[0])
        .join("")
        .toUpperCase()
    : "U";

  if (loading) {
    return (
      <div className="container mx-auto p-4 space-y-8">
        <div className="rounded-xl overflow-hidden">
          <Skeleton className="h-48 w-full" />
          <div className="flex flex-col md:flex-row gap-4 p-4">
            <Skeleton className="h-32 w-32 rounded-full" />
            <div className="space-y-4 flex-1">
              <Skeleton className="h-8 w-1/3" />
              <Skeleton className="h-4 w-1/4" />
              <div className="flex gap-2">
                <Skeleton className="h-10 w-28" />
                <Skeleton className="h-10 w-28" />
              </div>
            </div>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Skeleton className="h-64 rounded-lg" />
          <Skeleton className="h-64 rounded-lg" />
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-4 space-y-8 pb-16 animate-fade-in">
      {/* Profile header with banner and main info */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <div className="h-48 bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 relative">
          {/* Header decorative elements */}
          <div className="absolute inset-0 opacity-20">
            <div className="absolute top-1/3 left-1/4 w-24 h-24 rounded-full bg-white"></div>
            <div className="absolute top-1/2 right-1/3 w-16 h-16 rounded-full bg-white"></div>
            <div className="absolute bottom-1/4 right-1/4 w-20 h-20 rounded-full bg-white"></div>
          </div>
        </div>
        
        <div className="relative px-4 sm:px-6 pb-6">
          <div className="flex flex-col md:flex-row md:items-end gap-6 -mt-16">
            <div className="relative group">
              <Avatar className="h-32 w-32 border-4 border-white shadow-md ring-2 ring-primary/10">
                <AvatarImage src={profilePhoto || "/placeholder.svg"} alt={fullName || "Profile"} className="object-cover" />
                <AvatarFallback className="text-4xl bg-primary text-primary-foreground">{initials}</AvatarFallback>
              </Avatar>
              <div className="absolute inset-0 bg-black/40 group-hover:opacity-100 opacity-0 transition-opacity rounded-full flex items-center justify-center">
                <Button 
                  variant="ghost" 
                  size="icon"
                  className="text-white hover:text-primary hover:bg-white/10"
                  onClick={() => navigate("/profile/edit")}
                >
                  <FileEdit className="h-5 w-5" />
                </Button>
              </div>
            </div>
            
            <div className="flex-1 space-y-2">
              <h1 className="text-2xl font-bold mt-2 md:mt-0">
                {fullName || "Complétez votre profil"}
              </h1>
              <p className="text-muted-foreground capitalize">{type}</p>
            </div>
            
            <div className="flex flex-wrap gap-3 mt-4 md:mt-0 self-start md:self-center">
              <Button
                onClick={() => navigate("/profile/edit")}
                size="sm"
                className="shadow-sm"
              >
                <FileEdit className="mr-2 h-4 w-4" />
                Modifier
              </Button>
              
              <AdminButton />
              
              <Button
                onClick={handleSignOut}
                size="sm"
                variant="destructive"
                className="shadow-sm"
              >
                <LogOut className="mr-2 h-4 w-4" />
                Se déconnecter
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Main content with tabs */}
      <Tabs defaultValue="info" className="space-y-6">
        <TabsList className="bg-muted/80 backdrop-blur-sm p-1 rounded-lg">
          <TabsTrigger 
            value="info" 
            className="data-[state=active]:bg-white data-[state=active]:shadow-sm rounded-md transition-all"
          >
            Informations
          </TabsTrigger>
          <TabsTrigger 
            value="bio" 
            className="data-[state=active]:bg-white data-[state=active]:shadow-sm rounded-md transition-all"
          >
            Biographie
          </TabsTrigger>
          <TabsTrigger 
            value="stats" 
            className="data-[state=active]:bg-white data-[state=active]:shadow-sm rounded-md transition-all"
          >
            Statistiques
          </TabsTrigger>
        </TabsList>

        <TabsContent value="info" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Contact Information Card */}
            <Card className="overflow-hidden border-none shadow-md">
              <CardHeader className="bg-gradient-to-r from-sky-50 to-indigo-50 pb-2">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Mail className="h-5 w-5 text-primary" />
                  Informations de Contact
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 pt-6">
                <div className="flex items-start space-x-3">
                  <Mail className="h-5 w-5 text-muted-foreground shrink-0 mt-0.5" />
                  <div className="space-y-1">
                    <p className="text-sm font-medium">Email</p>
                    <p className="text-sm text-muted-foreground">{email || "Aucun email"}</p>
                  </div>
                </div>
                
                <Separator />
                
                <div className="flex items-start space-x-3">
                  <Phone className="h-5 w-5 text-muted-foreground shrink-0 mt-0.5" />
                  <div className="space-y-1">
                    <p className="text-sm font-medium">Téléphone</p>
                    <p className="text-sm text-muted-foreground">{phone || "Aucun numéro"}</p>
                  </div>
                </div>
                
                <Separator />
                
                <div className="flex items-start space-x-3">
                  <MapPin className="h-5 w-5 text-muted-foreground shrink-0 mt-0.5" />
                  <div className="space-y-1">
                    <p className="text-sm font-medium">Adresse</p>
                    <p className="text-sm text-muted-foreground break-words">{address || "Aucune adresse"}</p>
                  </div>
                </div>
                
                {website && (
                  <>
                    <Separator />
                    <div className="flex items-start space-x-3">
                      <Globe className="h-5 w-5 text-muted-foreground shrink-0 mt-0.5" />
                      <div className="space-y-1">
                        <p className="text-sm font-medium">Site Web</p>
                        <a
                          href={website}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-primary hover:underline truncate"
                        >
                          {website}
                        </a>
                      </div>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>

            {/* Professional Information Card */}
            <Card className="overflow-hidden border-none shadow-md">
              <CardHeader className="bg-gradient-to-r from-amber-50 to-yellow-50 pb-2">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Award className="h-5 w-5 text-primary" />
                  Profil Professionnel
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 pt-6">
                <div className="flex items-start space-x-3">
                  <UserRound className="h-5 w-5 text-muted-foreground shrink-0 mt-0.5" />
                  <div className="space-y-1">
                    <p className="text-sm font-medium">Type de compte</p>
                    <p className="text-sm text-muted-foreground capitalize">{type || "Non spécifié"}</p>
                  </div>
                </div>
                
                <Separator />
                
                <div className="flex items-start space-x-3">
                  <Shield className="h-5 w-5 text-muted-foreground shrink-0 mt-0.5" />
                  <div className="space-y-1">
                    <p className="text-sm font-medium">Statut de vérification</p>
                    <div className="flex items-center">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        Vérifié
                      </span>
                    </div>
                  </div>
                </div>
                
                <Separator />
                
                <div className="flex items-start space-x-3">
                  <Clock className="h-5 w-5 text-muted-foreground shrink-0 mt-0.5" />
                  <div className="space-y-1">
                    <p className="text-sm font-medium">Disponibilité</p>
                    <p className="text-sm text-muted-foreground">Lun-Ven, 9h-18h</p>
                  </div>
                </div>
                
                <Separator />
                
                <div className="flex items-start space-x-3">
                  <Calendar className="h-5 w-5 text-muted-foreground shrink-0 mt-0.5" />
                  <div className="space-y-1">
                    <p className="text-sm font-medium">Membre depuis</p>
                    <p className="text-sm text-muted-foreground">Janvier 2023</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="bio">
          <Card className="border-none shadow-md overflow-hidden">
            <CardHeader className="bg-gradient-to-r from-rose-50 to-teal-50 pb-2">
              <CardTitle className="flex items-center gap-2">
                <BookOpen className="h-5 w-5 text-primary" />
                Biographie
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              {bio ? (
                <p className="text-muted-foreground whitespace-pre-wrap leading-relaxed">
                  {bio}
                </p>
              ) : (
                <div className="text-center py-8">
                  <p className="text-muted-foreground">Aucune biographie disponible</p>
                  <p className="text-sm text-muted-foreground mt-2">
                    Ajoutez une biographie pour vous présenter aux autres utilisateurs
                  </p>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="mt-4"
                    onClick={() => navigate("/profile/edit")}
                  >
                    Ajouter une biographie
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="stats">
          <Card className="border-none shadow-md overflow-hidden">
            <CardHeader className="bg-gradient-to-r from-blue-50 to-cyan-50 pb-2">
              <CardTitle className="flex items-center gap-2">
                <Award className="h-5 w-5 text-primary" />
                Statistiques
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-muted/30 rounded-lg p-4 text-center">
                  <p className="text-3xl font-bold text-primary">12</p>
                  <p className="text-sm text-muted-foreground">Rendez-vous</p>
                </div>
                <div className="bg-muted/30 rounded-lg p-4 text-center">
                  <p className="text-3xl font-bold text-primary">24</p>
                  <p className="text-sm text-muted-foreground">Entrées journal</p>
                </div>
                <div className="bg-muted/30 rounded-lg p-4 text-center">
                  <p className="text-3xl font-bold text-primary">4</p>
                  <p className="text-sm text-muted-foreground">Médicaments</p>
                </div>
                <div className="bg-muted/30 rounded-lg p-4 text-center">
                  <p className="text-3xl font-bold text-primary">8</p>
                  <p className="text-sm text-muted-foreground">Messages</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ProfilePage;
